import { type AccountRegions } from '@payaca/types/accountTypes';
import { currencyPrice } from './financeHelper';

/**
 * A grant in the shape the grant-application helper needs. `'INVOICE'` is the
 * only deduction type the enum has today, so it is the only one subtracted from
 * the gross; the non-invoice guard in `applyInvoiceGrants` is a defensive seam
 * that keeps call sites stable when the enum gains, say, an informational-grant
 * type.
 *
 * `availableAmountMinorUnits` is the grant's face value. The amount it actually
 * deducts (its applied amount) is derived by the helper, capped to what remains
 * of the gross after earlier grants — see `grantAppliedAmounts`.
 */
export type InvoiceGrantInput = {
  deductionType: 'INVOICE';
  availableAmountMinorUnits: number;
};

export type AppliedInvoiceGrants = {
  /**
   * The gross still left after every invoice grant's *applied* amount has been
   * deducted. The starting gross is floored at `max(0, grossIncTax)`, so a
   * negative gross yields `0`; otherwise this is `grossIncTax` minus the sum of
   * the applied amounts. Never negative.
   */
  totalPayable: number;
  /**
   * The amount each grant actually deducts, parallel to the `grants` input
   * (same length, same order). An invoice grant's applied amount is its
   * available amount capped to the gross remaining after earlier grants; grants
   * that don't deduct from the gross (e.g. future informational grants)
   * contribute zero.
   */
  grantAppliedAmounts: number[];
};

/**
 * Pure helper which applies a set of grants to a gross (tax-inclusive) total to
 * derive the total payable and each grant's applied amount. Shared across Estimates,
 * Proposals, and Change Proposals so all three deduct grants identically.
 */
export const applyInvoiceGrants = ({
  grossIncTax,
  grants,
}: {
  grossIncTax: number;
  grants: InvoiceGrantInput[];
}): AppliedInvoiceGrants => {
  let remaining = Math.max(0, grossIncTax);
  const grantAppliedAmounts = grants.map((grant) => {
    if (grant.deductionType !== 'INVOICE') return 0;
    const applied = Math.min(grant.availableAmountMinorUnits, remaining);
    remaining -= applied;
    return applied;
  });

  return {
    totalPayable: remaining,
    grantAppliedAmounts,
  };
};

/**
 * A Project's invoice totals, as the calculated-totals pass stores them on
 * `deals`. Each counts only non-voided invoices; they differ in what else they
 * admit.
 */
export type ProjectInvoiceTotals = {
  /** Every invoice, sent or draft, customer or grant. */
  invoicedValue: number;
  /** Only invoices that have been sent, customer or grant. */
  sentInvoicedValue: number;
  /** Only invoices that have been sent and are **not** grant invoices. */
  customerInvoicedValue: number;
};

/**
 * What has already been invoiced to the Customer against `totalPayable`:
 * non-voided, non-grant invoices, **including unsent drafts**.
 *
 * No stored column holds this figure, so it is derived. `customerInvoicedValue`
 * excludes grants but also excludes drafts; `invoicedValue - sentInvoicedValue`
 * is the draft balance. Adding them gives drafts back without giving grants
 * back:
 *
 *     (sent, non-grant)  +  (drafts)  =  (sent or draft, non-grant)
 *
 * **This leans on a process truth, not a constraint.** The draft term carries
 * no grant invoices only because `markGrantAsPaid` creates a grant invoice with
 * `sentAt` already set, so a grant invoice is never a draft. Nothing in the
 * schema enforces that. If it ever stops holding, the draft term starts
 * including grant drafts and this figure over-counts — which shrinks the
 * invoiceable amount rather than growing it, so the failure is at least in the
 * safe direction.
 *
 * The derivation is clumsy for that reason and is worth replacing with a column
 * or a direct query when the invoice totals are next revisited.
 */
export const getCustomerInvoicedValueIncludingDrafts = ({
  invoicedValue,
  sentInvoicedValue,
  customerInvoicedValue,
}: ProjectInvoiceTotals): number =>
  customerInvoicedValue + (invoicedValue - sentInvoicedValue);

/**
 * The most that may still be invoiced to the Customer on a Project, never
 * negative.
 *
 * `totalPayable` is the gross less the Project's grants, so this is the figure
 * that stops an invoice encroaching on money a grant body is paying.
 *
 * Shared so every surface agrees on the number, and applied in the invoice
 * service so no route can bypass it.
 */
export const getMaxInvoiceableValue = ({
  totalPayable,
  ...totals
}: ProjectInvoiceTotals & { totalPayable: number }): number =>
  Math.max(0, totalPayable - getCustomerInvoicedValueIncludingDrafts(totals));

export const getInvoiceCapExceededMessage = ({
  maxValue,
  totalPayable,
  acceptedValue,
  region,
}: {
  maxValue: number;
  totalPayable: number;
  acceptedValue: number;
  region: AccountRegions;
}): string => {
  const max = currencyPrice(maxValue, region);
  return totalPayable < acceptedValue
    ? `This invoice can't be more than ${max}, the amount left to invoice the customer once grants are deducted`
    : `This invoice can't be more than ${max}, the amount left to invoice the customer`;
};
